import { useEffect, useState, useCallback } from "react";
import {
  IAnalysedProperty,
  IPropertyAnnualExpense,
  IPropertyAnnualRentalReturns,
} from "../../../../../interface/addAnalyser";
import { useAnalysedPropertyContext } from "../AnalysedPropertyContext";
import { REIInputNumber } from "../../../../../components/ui";
import InputSlider from "../../../../../components/ui/slider";
import { formatCurrencyWithDecimal } from "../../../../../utils/commonUtil";

interface IAnnualPropertyReturn {
  setIsValid: (isValid: boolean) => void;
  updateProperty: (updated: Partial<IAnalysedProperty>) => void;
  rentalReturnTotal: number;
}

const AnnualPropertyExpense: React.FC<IAnnualPropertyReturn> = ({ setIsValid, updateProperty, rentalReturnTotal }) => {
  const { analysedProperty, userProfile, setUserProfile } = useAnalysedPropertyContext();
  const [annualPropertyExpense, setAnnualPropertyExpense] = useState<IPropertyAnnualExpense | undefined>(undefined);
  const [propertyExpenseTotal, setPropertyExpenseTotal] = useState<number>();
  const [reset, setReset] = useState<boolean>(false);

  useEffect(() => {
    if (analysedProperty && !reset) {
      setAnnualPropertyExpense(analysedProperty.RentExpense.PropertyAnnualExpense);
      setReset(true);
    }
  }, [analysedProperty]);

  useEffect(() => {
    calculateTotalExpense(analysedProperty?.RentExpense.PropertyAnnualExpense);
  }, [rentalReturnTotal]);

  const isValueManuallyChanged = (name: string) => {
    const data = localStorage.getItem("annualPropertyExpense");
    if (data) {
      const parsedData = JSON.parse(data);
      return (parseFloat(parsedData[name]) || 0) > 0;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (
      analysedProperty?.LoanTypeCost?.PropertyPurchaseCost?.PurchaseAmount &&
      userProfile &&
      analysedProperty?.PropertyDetail.PropertyDetailId === 0
    ) {
      const ratesManuallyChanged = isValueManuallyChanged("Rates");
      const bodyCorpManuallyChanged = isValueManuallyChanged("BodyCorp");
      const insuranceManuallyChanged = isValueManuallyChanged("Insurance");
      const maintenanceManuallyChanged = isValueManuallyChanged("Maintenance");

      const purchaseAmount =
        parseFloat((analysedProperty?.LoanTypeCost?.PropertyPurchaseCost?.PurchaseAmount ?? "").toString()) || 0;

      setAnnualPropertyExpense((prevState) => ({
        ...prevState,
        Rates: ratesManuallyChanged
          ? JSON.parse(localStorage.getItem("annualPropertyExpense") || "{}").Rates
          : userProfile?.PropertyExpenseStandard.isRatePercentage == "1"
          ? (purchaseAmount * userProfile.PropertyExpenseStandard.RatesPercentage) / 100
          : userProfile.PropertyExpenseStandard.Rates,
        BodyCorp: bodyCorpManuallyChanged
          ? JSON.parse(localStorage.getItem("annualPropertyExpense") || "{}").BodyCorp
          : userProfile?.PropertyExpenseSpecific.isBodyCorporatePercentage == "1"
          ? (purchaseAmount * userProfile.PropertyExpenseSpecific.BodyCorpatePercentage) / 100
          : userProfile.PropertyExpenseSpecific.BodyCorporate,
        Insurance: insuranceManuallyChanged
          ? JSON.parse(localStorage.getItem("annualPropertyExpense") || "{}").Insurance
          : userProfile?.PropertyExpenseStandard.isInsurancePercentage == "1"
          ? (purchaseAmount * userProfile.PropertyExpenseStandard.InsurancePercentage) / 100
          : userProfile.PropertyExpenseStandard.Insurance,
        Maintenance: maintenanceManuallyChanged
          ? JSON.parse(localStorage.getItem("annualPropertyExpense") || "{}").Maintenance
          : userProfile?.PropertyExpenseStandard.isRepairPercentage == "1"
          ? (purchaseAmount * userProfile.PropertyExpenseStandard.RepairPercentage) / 100
          : userProfile.PropertyExpenseStandard.Repair,

        LandLease: prevState?.LandLease || 0,
        LettingFee: prevState?.LettingFee || 0,
        AgentComm: prevState?.AgentComm || 0,
        Gardening: prevState?.Gardening || 0,
        Cleaning: prevState?.Cleaning || 0,
        ServiceCont: prevState?.ServiceCont || 0,
        Other: prevState?.Other || 0,
      }));
    }
  }, [analysedProperty?.LoanTypeCost?.PropertyPurchaseCost?.PurchaseAmount]);

  useEffect(() => {
    if (
      analysedProperty?.PropertyDetail.PropertyDetailId === 0 &&
      analysedProperty?.PropertyDetail.useDefault == "1" &&
      userProfile
    ) {
      const purchaseAmount =
        parseFloat((analysedProperty?.LoanTypeCost?.PropertyPurchaseCost?.PurchaseAmount ?? "").toString()) || 0;

      const ratesManuallyChanged = isValueManuallyChanged("Rates");
      const bodyCorpManuallyChanged = isValueManuallyChanged("BodyCorp");
      const insuranceManuallyChanged = isValueManuallyChanged("Insurance");
      const maintenanceManuallyChanged = isValueManuallyChanged("Maintenance");

      const landLeaseManuallyChanged = isValueManuallyChanged("LandLease");
      const gardeningManuallyChanged = isValueManuallyChanged("Gardening");
      const cleaningManuallyChanged = isValueManuallyChanged("Cleaning");
      const serviceContManuallyChanged = isValueManuallyChanged("ServiceCont");
      const lettingFeeManuallyChanged = isValueManuallyChanged("LettingFee");
      const agentCommManuallyChanged = isValueManuallyChanged("AgentComm");
      const otherManuallyChanged = isValueManuallyChanged("Other");

      const newExpense = {
        Rates: ratesManuallyChanged
          ? JSON.parse(localStorage.getItem("annualPropertyExpense") || "{}").Rates
          : userProfile?.PropertyExpenseStandard.isRatePercentage == "1"
          ? (purchaseAmount * userProfile.PropertyExpenseStandard.RatesPercentage) / 100
          : userProfile.PropertyExpenseStandard.Rates,
        BodyCorp: bodyCorpManuallyChanged
          ? JSON.parse(localStorage.getItem("annualPropertyExpense") || "{}").BodyCorp
          : userProfile?.PropertyExpenseSpecific.isBodyCorporatePercentage == "1"
          ? (purchaseAmount * userProfile.PropertyExpenseSpecific.BodyCorpatePercentage) / 100
          : userProfile.PropertyExpenseSpecific.BodyCorporate,
        Insurance: insuranceManuallyChanged
          ? JSON.parse(localStorage.getItem("annualPropertyExpense") || "{}").Insurance
          : userProfile?.PropertyExpenseStandard.isInsurancePercentage == "1"
          ? (purchaseAmount * userProfile.PropertyExpenseStandard.InsurancePercentage) / 100
          : userProfile.PropertyExpenseStandard.Insurance,
        Maintenance: maintenanceManuallyChanged
          ? JSON.parse(localStorage.getItem("annualPropertyExpense") || "{}").Maintenance
          : userProfile?.PropertyExpenseStandard.isRepairPercentage == "1"
          ? (purchaseAmount * userProfile.PropertyExpenseStandard.RepairPercentage) / 100
          : userProfile.PropertyExpenseStandard.Repair,

        LandLease: landLeaseManuallyChanged
          ? JSON.parse(localStorage.getItem("annualPropertyExpense") || "{}").LandLease
          : userProfile?.PropertyExpenseSpecific.LandLease,
        LettingFee: lettingFeeManuallyChanged
          ? JSON.parse(localStorage.getItem("annualPropertyExpense") || "{}").LettingFee
          : userProfile?.PropertyExpenseStandard.LettingFee,
        AgentComm: agentCommManuallyChanged
          ? JSON.parse(localStorage.getItem("annualPropertyExpense") || "{}").AgentComm
          : userProfile?.PropertyExpenseStandard.AgentComm,
        Gardening: gardeningManuallyChanged
          ? JSON.parse(localStorage.getItem("annualPropertyExpense") || "{}").Gardening
          : userProfile?.PropertyExpenseSpecific.Gardening,
        Cleaning: cleaningManuallyChanged
          ? JSON.parse(localStorage.getItem("annualPropertyExpense") || "{}").Cleaning
          : userProfile?.PropertyExpenseStandard.Cleaning,
        ServiceCont: serviceContManuallyChanged
          ? JSON.parse(localStorage.getItem("annualPropertyExpense") || "{}").ServiceCont
          : userProfile?.PropertyExpenseStandard.ServiceCont,
        Other: otherManuallyChanged
          ? JSON.parse(localStorage.getItem("annualPropertyExpense") || "{}").Other
          : userProfile?.PropertyExpenseStandard.Others,
      };

      setAnnualPropertyExpense(newExpense);
    }
  }, [analysedProperty?.PropertyDetail.useDefault, userProfile]);

  useEffect(() => {
    if (annualPropertyExpense) {
      setIsValid(true);
      updateProperty({
        ...analysedProperty,
        RentExpense: {
          ...analysedProperty?.RentExpense,
          PropertyAnnualRentalReturns:
            analysedProperty?.RentExpense.PropertyAnnualRentalReturns ?? ({} as IPropertyAnnualRentalReturns),
          PropertyAnnualExpense: annualPropertyExpense,
        },
      });
      calculateTotalExpense(annualPropertyExpense);
    }
  }, [annualPropertyExpense]);

  const calculateTotalExpense = useCallback(
    (expense: IPropertyAnnualExpense | undefined) => {
      if (!expense) return;
      const { AgentComm, ...expenseWithoutAgentComm } = expense;
      let totalExpense = Object.values(expenseWithoutAgentComm).reduce(
        (acc: number, curr) => acc + (typeof curr === "number" ? curr : 0),
        0
      );
      const totalRentExpense = totalExpense + rentalReturnTotal * (AgentComm / 100);
      setPropertyExpenseTotal(parseInt(totalRentExpense.toFixed(0)));
    },
    [rentalReturnTotal]
  );

  const setValues = useCallback(
    (name: string, value: string | number) => {
      setAnnualPropertyExpense((prevState) => {
        if (!prevState) return undefined;
        const updatedExpense = {
          ...prevState,
          [name]: value !== undefined ? value : 0,
        };

        if (analysedProperty?.PropertyDetail.PropertyDetailId === 0) {
          const storedData = localStorage.getItem("annualPropertyExpense");
          let existingFields: Record<string, any> = {};

          if (storedData) {
            existingFields = JSON.parse(storedData);
          }

          const existingValue = existingFields[name] || 0;

          const updatedFields = {
            ...existingFields,
            [name]: existingValue > 0 ? existingValue : value,
          };

          localStorage.setItem("annualPropertyExpense", JSON.stringify(updatedFields));
        }

        calculateTotalExpense(updatedExpense);
        return updatedExpense;
      });
    },
    [calculateTotalExpense]
  );

  return (
    <div>
      <div className="relative rounded-lg pl-3 pr-1 py-3 border-solid border border-gray-300 bg-white mt-2">
        <div className="default-profile-header-container">
          <h3 className="text-[var(--primary-color)] mt-0 m-1 text-lg">Annual property expenses</h3>
        </div>
        <div className="p-4">
          <div className="flex flex-wrap mb-4">
            <div className="w-full md:w-1/2 p-2">
              <div className="flex items-center justify-evenly">
                <label className="w-1/3 text-left" htmlFor="Rates">
                  Rates ($)
                </label>
                <InputSlider
                  id="ape-sldr-rates"
                  value={
                    annualPropertyExpense?.Rates !== undefined
                      ? annualPropertyExpense.Rates > 5000
                        ? 5000
                        : annualPropertyExpense.Rates
                      : 0
                  }
                  onChange={(e) => setValues("Rates", Array.isArray(e.value) ? e.value[0] : e.value)}
                  classNames="w-1/2 mx-2"
                  min={0}
                  max={5000}
                  step={1}
                />
                <REIInputNumber
                  id="ape-num-rates"
                  value={annualPropertyExpense?.Rates}
                  onValueChange={(e) => setValues("Rates", e.target.value)}
                  useGrouping={true}
                  min={0}
                />
              </div>
            </div>
            <div className="w-full md:w-1/2 p-2" title="Add estimated annual cost.">
              <div className="flex items-center justify-evenly">
                <label className="w-1/3 text-left" htmlFor="BodyCorp">
                  Body corporate ($)
                </label>
                <InputSlider
                  id="ape-sldr-bodycorporate"
                  value={
                    annualPropertyExpense?.BodyCorp !== undefined
                      ? annualPropertyExpense.BodyCorp > 10000
                        ? 10000
                        : annualPropertyExpense.BodyCorp
                      : 0
                  }
                  onChange={(e) => setValues("BodyCorp", Array.isArray(e.value) ? e.value[0] : e.value)}
                  classNames="w-1/2 mx-2"
                  min={0}
                  max={10000}
                  step={1}
                />
                <REIInputNumber
                  id="ape-num-bodycorporate"
                  value={annualPropertyExpense?.BodyCorp}
                  onValueChange={(e) => setValues("BodyCorp", e.target.value)}
                  useGrouping={true}
                  min={0}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap mb-4">
            <div className="w-full md:w-1/2 p-2 justify-evenly">
              <div className="flex items-center">
                <label className="w-1/3 text-left" htmlFor="LandLease">
                  Land lease ($)
                </label>
                <InputSlider
                  id="ape-sldr-landlease"
                  value={
                    annualPropertyExpense?.LandLease !== undefined
                      ? annualPropertyExpense.LandLease > 50000
                        ? 50000
                        : annualPropertyExpense.LandLease
                      : 0
                  }
                  onChange={(e) => setValues("LandLease", Array.isArray(e.value) ? e.value[0] : e.value)}
                  classNames="w-1/2 mx-2"
                  min={0}
                  max={50000}
                  step={1000}
                />
                <REIInputNumber
                  id="ape-num-landlease"
                  value={annualPropertyExpense?.LandLease}
                  onValueChange={(e) => setValues("LandLease", e.target.value)}
                  useGrouping={true}
                  min={0}
                />
              </div>
            </div>
            <div className="w-full md:w-1/2 p-2">
              <div className="flex items-center justify-evenly">
                <label className="w-1/3 text-left" htmlFor="Insurance">
                  Insurance ($)
                </label>
                <InputSlider
                  id="ape-sldr-insurance"
                  value={
                    annualPropertyExpense?.Insurance !== undefined
                      ? annualPropertyExpense.Insurance > 1000
                        ? 1000
                        : annualPropertyExpense.Insurance
                      : 0
                  }
                  onChange={(e) => setValues("Insurance", Array.isArray(e.value) ? e.value[0] : e.value)}
                  classNames="w-1/2 mx-2"
                  min={0}
                  max={1000}
                  step={1}
                />
                <REIInputNumber
                  id="ape-num-insurance"
                  value={annualPropertyExpense?.Insurance}
                  onValueChange={(e) => setValues("Insurance", e.target.value)}
                  useGrouping={true}
                  min={0}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap mb-4">
            <div className="w-full md:w-1/2 p-2 justify-evenly">
              <div className="flex items-center">
                <label className="w-1/3 text-left" htmlFor="LettingFee">
                  Letting fees ($)
                </label>
                <InputSlider
                  id="ape-sldr-lettingfees"
                  value={
                    annualPropertyExpense?.LettingFee !== undefined
                      ? annualPropertyExpense.LettingFee > 3000
                        ? 3000
                        : annualPropertyExpense.LettingFee
                      : 0
                  }
                  onChange={(e) => setValues("LettingFee", Array.isArray(e.value) ? e.value[0] : e.value)}
                  classNames="w-1/2 mx-2"
                  min={0}
                  max={3000}
                  step={1}
                />
                <REIInputNumber
                  id="ape-num-lettingfees"
                  value={annualPropertyExpense?.LettingFee}
                  onValueChange={(e) => setValues("LettingFee", e.target.value)}
                  useGrouping={true}
                  min={0}
                />
              </div>
            </div>
            <div className="w-full md:w-1/2 p-2">
              <div className="flex items-center justify-evenly">
                <label className="w-1/3 text-left" htmlFor="AgentComm">
                  Management fees (%)
                </label>
                <InputSlider
                  id="ape-sldr-managementfees"
                  value={
                    annualPropertyExpense?.AgentComm !== undefined
                      ? annualPropertyExpense.AgentComm > 10
                        ? 10
                        : annualPropertyExpense.AgentComm
                      : 0
                  }
                  onChange={(e) => setValues("AgentComm", Array.isArray(e.value) ? e.value[0] : e.value)}
                  classNames="w-1/2 mx-2"
                  min={0.0}
                  max={10.0}
                  step={0.01}
                />
                <REIInputNumber
                  id="ape-num-managementfees"
                  value={annualPropertyExpense?.AgentComm}
                  onValueChange={(e) => setValues("AgentComm", e.target.value)}
                  useGrouping={true}
                  min={0.0}
                  maxFractionDigits={2}
                  max={100}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap mb-4">
            <div className="w-full md:w-1/2 p-2 justify-evenly">
              <div className="flex items-center">
                <label className="w-1/3 text-left" htmlFor="Maintenance">
                  Repairs/maintenance ($)
                </label>
                <InputSlider
                  id="ape-sldr-maintenance"
                  value={
                    annualPropertyExpense?.Maintenance !== undefined
                      ? annualPropertyExpense.Maintenance > 1000
                        ? 1000
                        : annualPropertyExpense.Maintenance
                      : 0
                  }
                  onChange={(e) => setValues("Maintenance", Array.isArray(e.value) ? e.value[0] : e.value)}
                  classNames="w-1/2 mx-2"
                  min={0}
                  max={1000}
                  step={1}
                />
                <REIInputNumber
                  id="ape-num-maintenance"
                  value={annualPropertyExpense?.Maintenance}
                  onValueChange={(e) => setValues("Maintenance", e.target.value)}
                  useGrouping={true}
                  min={0}
                />
              </div>
            </div>
            <div className="w-full md:w-1/2 p-2">
              <div className="flex items-center justify-evenly">
                <label className="w-1/3 text-left" htmlFor="Gardening">
                  Gardening ($)
                </label>
                <InputSlider
                  id="ape-sldr-gardening"
                  value={
                    annualPropertyExpense?.Gardening !== undefined
                      ? annualPropertyExpense.Gardening > 3000
                        ? 3000
                        : annualPropertyExpense.Gardening
                      : 0
                  }
                  onChange={(e) => setValues("Gardening", Array.isArray(e.value) ? e.value[0] : e.value)}
                  classNames="w-1/2 mx-2"
                  min={0}
                  max={3000}
                  step={1}
                />
                <REIInputNumber
                  id="ape-num-gardening"
                  value={annualPropertyExpense?.Gardening}
                  onValueChange={(e) => setValues("Gardening", e.target.value)}
                  useGrouping={true}
                  min={0}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap mb-4">
            <div className="w-full md:w-1/2 p-2 justify-evenly">
              <div className="flex items-center">
                <label className="w-1/3 text-left" htmlFor="Cleaning">
                  Cleaning ($)
                </label>
                <InputSlider
                  id="ape-sldr-cleaning"
                  value={
                    annualPropertyExpense?.Cleaning !== undefined
                      ? annualPropertyExpense.Cleaning > 1000
                        ? 1000
                        : annualPropertyExpense.Cleaning
                      : 0
                  }
                  onChange={(e) => setValues("Cleaning", Array.isArray(e.value) ? e.value[0] : e.value)}
                  classNames="w-1/2 mx-2"
                  min={0}
                  max={1000}
                  step={1}
                />
                <REIInputNumber
                  id="ape-num-cleaning"
                  value={annualPropertyExpense?.Cleaning}
                  onValueChange={(e) => setValues("Cleaning", e.target.value)}
                  useGrouping={true}
                  min={0}
                />
              </div>
            </div>
            <div className="w-full md:w-1/2 p-2">
              <div className="flex items-center justify-evenly">
                <label className="w-1/3 text-left" htmlFor="ServiceCont">
                  Service contracts ($)
                </label>
                <InputSlider
                  id="ape-sldr-servicecontracts"
                  value={
                    annualPropertyExpense?.ServiceCont !== undefined
                      ? annualPropertyExpense.ServiceCont > 1000
                        ? 1000
                        : annualPropertyExpense.ServiceCont
                      : 0
                  }
                  onChange={(e) => setValues("ServiceCont", Array.isArray(e.value) ? e.value[0] : e.value)}
                  classNames="w-1/2 mx-2"
                  min={0}
                  max={1000}
                  step={1}
                />
                <REIInputNumber
                  id="ape-num-servicecontracts"
                  value={annualPropertyExpense?.ServiceCont}
                  onValueChange={(e) => setValues("ServiceCont", e.target.value)}
                  useGrouping={true}
                  min={0}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap mb-4">
            <div className="w-full md:w-1/2 p-2 justify-evenly">
              <div className="flex items-center">
                <label className="w-1/3 text-left" htmlFor="Maintenance">
                  Other ($)
                </label>
                <InputSlider
                  id="ape-sldr-other"
                  value={
                    annualPropertyExpense?.Other !== undefined
                      ? annualPropertyExpense.Other > 100000
                        ? 100000
                        : annualPropertyExpense.Other < -100000
                        ? -100000
                        : annualPropertyExpense.Other
                      : 0
                  }
                  onChange={(e) => setValues("Other", Array.isArray(e.value) ? e.value[0] : e.value)}
                  classNames="w-1/2 mx-2"
                  min={-100000}
                  max={100000}
                  step={1}
                />
                <REIInputNumber
                  id="ape-num-other"
                  value={annualPropertyExpense?.Other}
                  onValueChange={(e) => setValues("Other", e.target.value)}
                  useGrouping={true}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap mb-4">
            <div className="w-full md:w-1/2 p-2">
              <p>
                <strong>
                  Total annual property expenses =
                  <span className="total_annual_property_expenses">{`${formatCurrencyWithDecimal(
                    propertyExpenseTotal ?? 0
                  )}`}</span>
                </strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnualPropertyExpense;
